import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { Star, Heart, Calendar } from "lucide-react";

export default function CustomerFavourites() {
  const [therapists, setTherapists] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/therapists")
      .then((r) => setTherapists(r.data.slice(0, 8)))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="premium-spinner mb-3"></div>
        <div className="text-xs text-amber-400 font-bold uppercase tracking-widest animate-pulse">Loading Therapists...</div>
      </div>
    );
  }

  return (
    <div data-testid="customer-favourites" className="space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="border-b-2 border-white/10 pb-6"
      >
        <div className="text-xs uppercase tracking-[0.25em] text-emerald-400 font-extrabold mb-1 flex items-center gap-2">
          <Heart size={16} className="text-rose-400" /> CURATED SELECTION · TOP THERAPISTS
        </div>
        <h1 className="text-4xl lg:text-5xl font-heading text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-hgold to-emerald-400 font-bold">
          Favourite Therapists
        </h1>
      </motion.div>

      {/* Grid of Therapists */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6"
      >
        {therapists.map((t) => (
          <div
            key={t.id}
            className="rounded-2xl border border-amber-400/70 overflow-hidden bg-gradient-to-b from-hpaper via-black/90 to-hpaper shadow-xl shadow-yellow-950/20 hover:scale-[1.02] transition-all flex flex-col justify-between"
          >
            <div>
              <div className="aspect-[3/4] relative overflow-hidden bg-black/60">
                <img
                  src={t.photos?.[0] || "https://images.pexels.com/photos/3757942/pexels-photo-3757942.jpeg"}
                  alt={t.display_name}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-80" />
                <div className="absolute top-3 right-3 bg-amber-500/90 text-black p-2 rounded-xl shadow-md">
                  <Heart size={14} fill="currentColor" />
                </div>
                <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
                  <span className="text-[10px] font-extrabold uppercase tracking-widest px-2.5 py-1 rounded-full border border-amber-400/50 bg-amber-950/90 text-amber-300 shadow-md flex items-center gap-1">
                    <Star size={11} fill="currentColor" className="text-amber-400" />
                    {t.rating?.toFixed(1) || 4.9}
                  </span>
                  <span className="text-[10px] font-bold text-white/80 bg-black/80 px-2 py-0.5 rounded">
                    {t.experience_years}y Exp
                  </span>
                </div>
              </div>

              <div className="p-4 space-y-1">
                <h3 className="font-heading text-xl font-bold text-white">{t.display_name}</h3>
                <div className="text-xs text-white/60 capitalize font-medium">{t.body_type || "Athletic"} · {t.role_preference || "Versatile"}</div>
              </div>
            </div>

            <div className="p-4 pt-0">
              <Link
                to={`/book?therapist=${t.id}`}
                className="w-full py-2.5 rounded-xl border border-amber-400/60 bg-amber-950/30 hover:bg-amber-500/20 text-amber-300 text-xs font-extrabold uppercase tracking-wider transition-all flex items-center justify-center gap-1.5"
              >
                <Calendar size={13} /> Book Session
              </Link>
            </div>
          </div>
        ))}
      </motion.div>
    </div>
  );
}
